import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/apiClient';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const CSV_DATASETS = [
  { value: 'users_overview', label: 'Users Overview' },
  { value: 'user_activity_summary', label: 'User Activity Summary' },
  { value: 'data_quality_issues', label: 'Data Quality Issues' },
  { value: 'plan_revenue_history', label: 'Plan Revenue History' },
];

const CUSTOMER_GROWTH_SERIES = [
  { key: 'free_customers', label: 'Free', className: 'admin-client-line-free' },
  { key: 'pro_customers', label: 'Pro', className: 'admin-client-line-pro' },
  { key: 'premium_customers', label: 'Premium', className: 'admin-client-line-premium' },
];

const TREND_CHART = {
  width: 760,
  height: 280,
  padding: { top: 18, right: 28, bottom: 52, left: 58 },
};

function formatNumber(value) {
  if (value == null) return 'Not available';
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue.toLocaleString() : 'Not available';
}

function formatCurrency(value) {
  if (value == null) return 'Not available';
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return 'Not available';
  return numberValue.toLocaleString(undefined, {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString();
  } catch (err) {
    return value;
  }
}

function formatDateTime(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return value;
  }
}

function getMarketingDateRange() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  const toInputDate = value => value.toISOString().slice(0, 10);
  return { start_date: toInputDate(start), end_date: toInputDate(end) };
}

function getTrendX(index, total) {
  const plotWidth = TREND_CHART.width - TREND_CHART.padding.left - TREND_CHART.padding.right;
  return total <= 1
    ? TREND_CHART.padding.left + plotWidth / 2
    : TREND_CHART.padding.left + (index / (total - 1)) * plotWidth;
}

function getTrendY(value, maxValue) {
  const plotHeight = TREND_CHART.height - TREND_CHART.padding.top - TREND_CHART.padding.bottom;
  const normalizedValue = maxValue > 0 ? Number(value || 0) / maxValue : 0;
  return TREND_CHART.padding.top + plotHeight - normalizedValue * plotHeight;
}

function getTrendTicks(length) {
  if (length <= 6) return Array.from({ length }, (_, index) => index);
  const step = Math.ceil((length - 1) / 5);
  const ticks = new Set([0, length - 1]);
  for (let index = step; index < length - 1; index += step) ticks.add(index);
  return Array.from(ticks).sort((a, b) => a - b);
}

function getYAxisValues(maxValue) {
  const safeMax = Math.max(1, Math.ceil(maxValue || 0));
  if (safeMax <= 4) return Array.from({ length: safeMax + 1 }, (_, index) => safeMax - index);
  return Array.from(new Set([safeMax, Math.round(safeMax * 0.75), Math.round(safeMax * 0.5), Math.round(safeMax * 0.25), 0]))
    .sort((a, b) => b - a);
}

function TimeSeriesLineChart({ rows, series }) {
  const maxValue = rows.reduce(
    (max, row) => Math.max(max, ...series.map(item => Number(row[item.key] || 0))), 0,
  );
  const yAxisValues = getYAxisValues(maxValue);
  const xAxisTicks = getTrendTicks(rows.length);
  const pointsFor = (key) => rows.map((row, index) => (
    `${getTrendX(index, rows.length).toFixed(2)},${getTrendY(row[key], maxValue || 1).toFixed(2)}`
  )).join(' ');

  return (
    <div className="admin-client-line-chart-card">
      <svg className="admin-client-line-chart" viewBox={`0 0 ${TREND_CHART.width} ${TREND_CHART.height}`} role="img" aria-label="Customer growth by group">
        {yAxisValues.map(value => {
          const y = getTrendY(value, maxValue || 1);
          return (
            <g key={value}>
              <line className="admin-client-chart-grid" x1={TREND_CHART.padding.left} x2={TREND_CHART.width - TREND_CHART.padding.right} y1={y} y2={y} />
              <text className="admin-client-chart-axis-text" x={TREND_CHART.padding.left - 10} y={y + 4} textAnchor="end">{formatNumber(value)}</text>
            </g>
          );
        })}
        {xAxisTicks.map(index => (
          <text className="admin-client-chart-axis-text" key={rows[index].period} x={getTrendX(index, rows.length)} y={TREND_CHART.height - 18} textAnchor="middle">
            {formatDate(rows[index].period)}
          </text>
        ))}
        {series.map(item => <polyline className={`admin-client-chart-line ${item.className}`} key={item.key} points={pointsFor(item.key)} />)}
        {series.map(item => rows.map((row, index) => (
          <circle className={`admin-client-chart-point ${item.className}`} key={`${item.key}-${row.period}`} cx={getTrendX(index, rows.length)} cy={getTrendY(row[item.key], maxValue || 1)} r="3.5">
            <title>{`${item.label}: ${formatNumber(row[item.key])} on ${formatDate(row.period)}`}</title>
          </circle>
        )))}
      </svg>
      <div className="admin-client-legend">
        {series.map(item => <span key={item.key}><i className={`admin-client-line-swatch ${item.className}`} /> {item.label}</span>)}
      </div>
    </div>
  );
}

function getFilenameFromDisposition(disposition, fallback) {
  if (!disposition) return fallback;
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch) return decodeURIComponent(utfMatch[1].replace(/"/g, ''));
  const match = disposition.match(/filename="?([^"]+)"?/i);
  return match ? match[1] : fallback;
}

function AdminClientAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportForm, setExportForm] = useState({ dataset: 'users_overview', start_date: '', end_date: '', limit: '1000' });
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportSuccess, setExportSuccess] = useState('');
  const [marketingRange, setMarketingRange] = useState(getMarketingDateRange);
  const navigate = useNavigate();

  const fetchAnalytics = useCallback(async ({ showLoading = true } = {}) => {
    if (showLoading) setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (marketingRange.start_date) params.set('start_date', marketingRange.start_date);
      if (marketingRange.end_date) params.set('end_date', marketingRange.end_date);
      const query = params.toString();
      const response = await apiFetch(`${API_BASE_URL}/admin/client-analytics/summary${query ? `?${query}` : ''}`, { method: 'GET' });
      if (response.status === 403) {
        navigate('/dashboard');
        return;
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to load client analytics');
      }
      setAnalytics(await response.json());
    } catch (err) {
      setError(err.message || 'Failed to load client analytics');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [marketingRange.end_date, marketingRange.start_date, navigate]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);
  useEffect(() => {
    const refresh = () => fetchAnalytics({ showLoading: false });
    const onVisibility = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchAnalytics]);

  const handleExportChange = (field, value) => {
    setExportForm(previous => ({ ...previous, [field]: value }));
    setExportError('');
    setExportSuccess('');
  };

  const handleCsvExport = async (event) => {
    event.preventDefault();
    setExportLoading(true);
    setExportError('');
    setExportSuccess('');
    try {
      const params = new URLSearchParams();
      params.set('dataset', exportForm.dataset);
      if (exportForm.start_date) params.set('start_date', exportForm.start_date);
      if (exportForm.end_date) params.set('end_date', exportForm.end_date);
      if (exportForm.limit) params.set('limit', exportForm.limit);
      const response = await apiFetch(`${API_BASE_URL}/admin/client-analytics/export.csv?${params.toString()}`, { method: 'GET' });
      if (response.status === 403) {
        navigate('/dashboard');
        return;
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to export CSV');
      }
      const blob = await response.blob();
      const filename = getFilenameFromDisposition(response.headers.get('Content-Disposition'), `admin-client-analytics-${exportForm.dataset}.csv`);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setExportSuccess(`Downloaded ${filename}`);
    } catch (err) {
      setExportError(err.message || 'Failed to export CSV');
    } finally {
      setExportLoading(false);
    }
  };

  const kpis = analytics?.summary_kpis || {};
  const growth = analytics?.customer_growth || { available: false, rows: [] };
  const conversion = analytics?.conversion || { available: false, message: 'Not available yet' };
  const productUsage = analytics?.product_usage || [];
  const hasGrowthChart = growth.available && growth.rows?.length >= 2;
  const dataLimitations = useMemo(() => analytics?.data_limitations || [], [analytics]);
  const marketing = analytics?.marketing_acquisition || { available: false, rows: [], note: 'Marketing acquisition data is not available yet.' };
  const marketingRows = marketing.rows || [];

  if (loading) {
    return <div className="admin-quality-page authenticated-page"><div className="admin-quality-container authenticated-page-content"><div className="admin-quality-header"><h1 className="admin-quality-title">Admin / Client Analytics</h1></div><p className="admin-quality-loading">Loading client analytics...</p></div></div>;
  }
  if (error) {
    return <div className="admin-quality-page authenticated-page"><div className="admin-quality-container authenticated-page-content"><div className="admin-quality-header"><h1 className="admin-quality-title">Admin / Client Analytics</h1></div><div className="admin-quality-error">{error}</div></div></div>;
  }

  return (
    <div className="admin-quality-page authenticated-page">
      <div className="admin-quality-container authenticated-page-content">
        <div className="admin-quality-header admin-client-header">
          <div>
            <h1 className="admin-quality-title">Admin / Client Analytics</h1>
            <p className="admin-quality-subtitle">Verified self-serve customer analytics from persisted application data.</p>
          </div>
          {analytics?.generated_at && <p className="admin-client-generated">Summary generated {formatDateTime(analytics.generated_at)}</p>}
        </div>

        <div className="admin-quality-section admin-client-compact-section">
          <div className="admin-quality-metrics admin-client-kpi-grid admin-client-executive-grid">
            <div className="admin-quality-metric-card"><h3>Free customers</h3><div className="admin-quality-metric-value">{formatNumber(kpis.free_customers)}</div></div>
            <div className="admin-quality-metric-card"><h3>Paying customers</h3><div className="admin-quality-metric-value">{formatNumber(kpis.paying_customers)}</div></div>
            <div className="admin-quality-metric-card"><h3>New sign-ups · 30 days</h3><div className="admin-quality-metric-value">{formatNumber(kpis.new_signups_30d)}</div></div>
            <div className="admin-quality-metric-card"><h3>Monthly revenue</h3><div className="admin-quality-metric-value">{formatCurrency(kpis.monthly_revenue)}</div><p className="admin-client-section-note">Collected this calendar month.</p></div>
          </div>
        </div>

        <div className="admin-quality-section admin-client-compact-section" data-testid="marketing-acquisition-section">
          <div className="admin-quality-section-header admin-client-marketing-header">
            <div>
              <h2>Marketing acquisition</h2>
              <p className="admin-client-section-note">First-touch website visits attributed to registrations and current plan status.</p>
            </div>
            <div className="admin-client-marketing-date-filters" aria-label="Marketing acquisition date range">
              <label className="admin-quality-label" htmlFor="marketing-start-date">Start date</label>
              <input
                id="marketing-start-date"
                className="admin-quality-input"
                type="date"
                value={marketingRange.start_date}
                onChange={event => setMarketingRange(previous => ({ ...previous, start_date: event.target.value }))}
              />
              <label className="admin-quality-label" htmlFor="marketing-end-date">End date</label>
              <input
                id="marketing-end-date"
                className="admin-quality-input"
                type="date"
                value={marketingRange.end_date}
                onChange={event => setMarketingRange(previous => ({ ...previous, end_date: event.target.value }))}
              />
            </div>
          </div>
          {!marketing.available && <p className="admin-quality-empty">{marketing.note || 'Marketing acquisition data is not available yet.'}</p>}
          {marketing.available && marketingRows.length === 0 && <p className="admin-quality-empty">No marketing visits were recorded in this date range.</p>}
          {marketing.available && marketingRows.length > 0 && (
            <div className="admin-client-compact-table-wrap">
              <table className="admin-quality-table admin-client-compact-table" aria-label="Marketing acquisition summary">
                <thead><tr><th>Source/post</th><th>Website visits</th><th>Registrations</th><th>Free users</th><th>Paid users</th></tr></thead>
                <tbody>
                  {marketingRows.map(row => (
                    <tr key={row.source_post}>
                      <td>{row.source_post}</td>
                      <td>{formatNumber(row.website_visits)}</td>
                      <td>{formatNumber(row.registrations)}</td>
                      <td>{formatNumber(row.free_users)}</td>
                      <td>{formatNumber(row.paid_users)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {marketing.note && marketing.available && <p className="admin-client-muted-note">{marketing.note}</p>}
        </div>

        <div className="admin-quality-section admin-client-compact-section">
          <div className="admin-quality-section-header"><div><h2>Customer growth</h2><p className="admin-client-section-note">Free, Pro, and Premium self-serve customer accounts over the last 30 days.</p></div></div>
          {hasGrowthChart ? <TimeSeriesLineChart rows={growth.rows} series={CUSTOMER_GROWTH_SERIES} /> : <p className="admin-quality-empty">{growth.note || 'Customer-growth history is not available yet.'}</p>}
          <p className="admin-client-muted-note">{conversion.message}</p>
        </div>

        <div className="admin-quality-section admin-client-compact-section">
          <div className="admin-quality-section-header"><h2>Product use by customer group</h2></div>
          <div className="admin-client-compact-table-wrap">
            <table className="admin-quality-table admin-client-compact-table" aria-label="Product use by customer group">
              <thead><tr><th>Customer group</th><th>Customers</th><th>Users with scans</th><th>Saved scans</th><th>Portfolios</th><th>Holdings</th><th>Active alerts</th></tr></thead>
              <tbody>
                {productUsage.map(row => <tr key={row.group}><td>{row.group}</td><td>{formatNumber(row.customers)}</td><td>{formatNumber(row.users_with_scans)}</td><td>{formatNumber(row.saved_scans)}</td><td>{formatNumber(row.portfolios)}</td><td>{formatNumber(row.holdings)}</td><td>{formatNumber(row.active_alerts)}</td></tr>)}
              </tbody>
            </table>
          </div>
          {dataLimitations.map(note => <p className="admin-client-muted-note" key={note}>{note}</p>)}
        </div>

        <div className="admin-quality-section admin-client-compact-section">
          <div className="admin-quality-section-header">
            <h2>Admin Tools / CSV Exports</h2>
          </div>
          {exportSuccess && <div className="admin-quality-success">{exportSuccess}</div>}
          {exportError && <div className="admin-quality-error">{exportError}</div>}
          <form onSubmit={handleCsvExport} className="admin-quality-form-card admin-client-export-card">
            <div className="admin-quality-form-grid">
              <div className="admin-quality-field">
                <label className="admin-quality-label">Dataset</label>
                <select
                  className="admin-quality-select"
                  value={exportForm.dataset}
                  onChange={(e) => handleExportChange('dataset', e.target.value)}
                >
                  {CSV_DATASETS.map(dataset => (
                    <option key={dataset.value} value={dataset.value}>{dataset.label}</option>
                  ))}
                </select>
              </div>
              <div className="admin-quality-field">
                <label className="admin-quality-label">Limit</label>
                <input
                  className="admin-quality-input"
                  type="number"
                  min="1"
                  max="10000"
                  value={exportForm.limit}
                  onChange={(e) => handleExportChange('limit', e.target.value)}
                />
              </div>
              <div className="admin-quality-field">
                <label className="admin-quality-label">Start Date</label>
                <input
                  className="admin-quality-input"
                  type="date"
                  value={exportForm.start_date}
                  onChange={(e) => handleExportChange('start_date', e.target.value)}
                />
              </div>
              <div className="admin-quality-field">
                <label className="admin-quality-label">End Date</label>
                <input
                  className="admin-quality-input"
                  type="date"
                  value={exportForm.end_date}
                  onChange={(e) => handleExportChange('end_date', e.target.value)}
                />
              </div>
            </div>
            <div className="admin-client-export-actions">
              <button type="submit" className="admin-quality-btn-primary" disabled={exportLoading}>
                {exportLoading ? 'Exporting...' : 'Download CSV'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminClientAnalytics;
